import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { tokenManager } from './tokenManager';
import type {
  User,
  AuthResponse,
  LoginRequest,
  Order,
  CreateOrderRequest,
  OrderFilters,
  Product,
  InventoryLevel,
  InventoryFilters,
  Shipment,
  Route,
  Vehicle,
  OrderStats,
  FulfillmentOptions,
  FulfillmentForm,
  StockAdjustmentForm,
  WebhookEvent,
  WebhookStats,
  WebhookFilters,
  DashboardStats,
  TrackingEvent,
  ApiResponse,
  ApiError,
  StickyNote,
} from '@/types';

class ApiService {
  public client: AxiosInstance;
  private baseURL: string;
  private refreshTimeoutId: number | null = null;

  constructor() {
    // Determine the correct API base URL.
    // In Vite dev mode (npm run dev), connect directly to the Flask backend.
    // In production builds served by nginx, use relative /api so nginx proxies correctly
    // and the CSP connect-src 'self' directive is not violated.
    if (import.meta.env.DEV) {
      // Vite dev server — direct connection to Flask backend
      this.baseURL = 'http://localhost:5001/api';
    } else {
      // Production (nginx) — relative path, proxied to backend, same origin
      this.baseURL = '/api';
    }
    
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token with automatic refresh
    this.client.interceptors.request.use(
      async (config) => {
        const token = await tokenManager.getValidToken(this.performRefresh.bind(this));
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Try to refresh token once
          try {
            const newToken = await tokenManager.refreshAccessToken(this.performRefresh.bind(this));
            if (newToken && error.config) {
              // Retry the original request with new token
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return this.client.request(error.config);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login only if not already on login page
            tokenManager.clearTokens();
            if (this.refreshTimeoutId) {
              clearTimeout(this.refreshTimeoutId);
              this.refreshTimeoutId = null;
            }
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );

    // Schedule token refresh on initialization
    this.scheduleTokenRefresh();
  }

  private async performRefresh(refreshToken: string): Promise<{ access_token: string; expires_in: number }> {
    const response = await axios.post(`${this.baseURL}/auth/refresh`, {}, {
      headers: {
        'Authorization': `Bearer ${refreshToken}`
      }
    });
    return response.data;
  }

  private scheduleTokenRefresh(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }
    
    this.refreshTimeoutId = tokenManager.scheduleTokenRefresh(this.performRefresh.bind(this));
  }

  private handleError(error: AxiosError): ApiError {
    const errorData = (error.response?.data as any)?.error;
    // errorData may be a string or an object like {code, message, status, details}
    const message = typeof errorData === 'string'
      ? errorData
      : errorData?.message || error.message || 'An unexpected error occurred';
    const details = (typeof errorData === 'object' ? errorData?.details : undefined)
      || (error.response?.data as any)?.details;
    const status = error.response?.status;

    return {
      message,
      details,
      status,
    };
  }

  // Authentication
  async login(credentials: LoginRequest, rememberMe: boolean = false): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/login', credentials);
    const { access_token, refresh_token, user, expires_in } = response.data;
    
    // Calculate expiration time
    const expiresAt = Date.now() + (expires_in * 1000);
    
    // Store tokens using token manager
    tokenManager.setTokens({
      access_token,
      refresh_token,
      expires_at: expiresAt,
      user
    }, rememberMe);
    
    // Schedule automatic refresh
    this.scheduleTokenRefresh();
    
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } finally {
      tokenManager.clearTokens();
      if (this.refreshTimeoutId) {
        clearTimeout(this.refreshTimeoutId);
        this.refreshTimeoutId = null;
      }
    }
  }

  async getProfile(): Promise<User> {
    const response = await this.client.get<{ user: User }>('/auth/profile');
    return response.data.user;
  }

  async refreshToken(): Promise<string> {
    return tokenManager.refreshAccessToken(this.performRefresh.bind(this));
  }

  // =============================================
  // User Management (Admin/Manager)
  // =============================================

  async getUsers(filters: any = {}): Promise<ApiResponse<User[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{ users: User[], pagination: any }>(`/auth/users?${params}`);
    return {
      data: response.data.users,
      pagination: response.data.pagination
    };
  }

  async getUser(id: number): Promise<User> {
    const response = await this.client.get<{ user: User }>(`/auth/users/${id}`);
    return response.data.user;
  }

  async createUser(userData: any): Promise<{ user: User; temp_password?: string }> {
    const response = await this.client.post<{ user: User; temp_password?: string }>('/auth/users', userData);
    return response.data;
  }

  async updateUser(id: number, userData: any): Promise<User> {
    const response = await this.client.put<{ user: User }>(`/auth/users/${id}`, userData);
    return response.data.user;
  }

  async deleteUser(id: number): Promise<void> {
    await this.client.delete(`/auth/users/${id}`);
  }

  async resetUserPassword(id: number, newPassword?: string): Promise<{ temp_password?: string }> {
    const response = await this.client.post<{ temp_password?: string }>(`/auth/users/${id}/reset-password`, newPassword ? { new_password: newPassword } : {});
    return response.data;
  }

  async unlockUser(id: number): Promise<void> {
    await this.client.post(`/auth/users/${id}/unlock`);
  }

  // =============================================
  // Session Management
  // =============================================

  async getSessions(): Promise<any[]> {
    const response = await this.client.get<{ sessions: any[] }>('/auth/sessions');
    return response.data.sessions;
  }

  async terminateSession(sessionId: number): Promise<void> {
    await this.client.delete(`/auth/sessions/${sessionId}`);
  }

  async getUserSessions(userId: number): Promise<any[]> {
    const response = await this.client.get<{ sessions: any[] }>(`/auth/users/${userId}/sessions`);
    return response.data.sessions;
  }

  async terminateUserSessions(userId: number): Promise<void> {
    await this.client.delete(`/auth/users/${userId}/sessions`);
  }

  // Orders
  async getOrders(filters: OrderFilters = {}): Promise<ApiResponse<Order[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{ orders: Order[], pagination: any }>(`/orders?${params}`);
    return {
      data: response.data.orders,
      pagination: response.data.pagination
    };
  }

  async getOrderWorkflowSettings(): Promise<any> {
    const response = await this.client.get('/settings/order-workflow');
    return response.data;
  }

  async updateOrderWorkflowSettings(payload: any): Promise<any> {
    const response = await this.client.put('/settings/order-workflow', payload);
    return response.data;
  }

  async previewOrderWorkflow(payload: any): Promise<any> {
    const response = await this.client.post('/orders/workflow/preview', payload);
    return response.data;
  }

  async getOrder(id: number): Promise<Order> {
    const response = await this.client.get<Order>(`/orders/${id}`);
    return response.data;
  }

  async createOrder(orderData: CreateOrderRequest): Promise<Order> {
    const response = await this.client.post<{ order: Order }>('/orders', orderData);
    return response.data.order;
  }

  async updateOrder(id: number, orderData: Partial<Order>): Promise<Order> {
    const response = await this.client.put<{ order: Order }>(`/orders/${id}`, orderData);
    return response.data.order;
  }

  async updateOrderStatus(id: number, status: string, notes?: string, onHoldType?: string): Promise<Order> {
    const payload: any = { status, notes };
    if (onHoldType !== undefined) {
      payload.on_hold_type = onHoldType;
    }
    const response = await this.client.put<{ order: Order }>(`/orders/${id}/status`, payload);
    return response.data.order;
  }

  async cancelOrder(id: number, reason?: string): Promise<Order> {
    const response = await this.client.post<{ order: Order }>(`/orders/${id}/cancel`, {
      reason,
    });
    return response.data.order;
  }

  // Sticky Notes
  async getStickyNotes(orderId: number): Promise<StickyNote[]> {
    const response = await this.client.get<{ sticky_notes: StickyNote[] }>(`/orders/${orderId}/sticky-notes`);
    return response.data.sticky_notes || [];
  }

  async createStickyNote(orderId: number, data: { note_text: string; priority?: string }): Promise<StickyNote> {
    const response = await this.client.post<{ sticky_note: StickyNote }>(`/orders/${orderId}/sticky-notes`, data);
    return response.data.sticky_note;
  }

  async updateStickyNote(orderId: number, noteId: number, data: { note_text?: string; priority?: string }): Promise<StickyNote> {
    const response = await this.client.put<{ sticky_note: StickyNote }>(`/orders/${orderId}/sticky-notes/${noteId}`, data);
    return response.data.sticky_note;
  }

  async deleteStickyNote(orderId: number, noteId: number): Promise<void> {
    await this.client.delete(`/orders/${orderId}/sticky-notes/${noteId}`);
  }

  async sendPickupEmail(orderId: number, from?: string): Promise<{ message: string; task_id: string; email: string; from: string }> {
    const response = await this.client.post<{ message: string; task_id: string; email: string; from: string }>(
      `/orders/${orderId}/send-pickup-email`,
      from ? { from } : {}
    );
    return response.data;
  }

  async emailInvoice(orderId: number, to?: string): Promise<{ message: string; task_id: string; email: string }> {
    const response = await this.client.post<{ message: string; task_id: string; email: string }>(
      `/orders/${orderId}/email-invoice`,
      to ? { to } : {}
    );
    return response.data;
  }

  async downloadInvoicePdf(orderId: number, orderNumber: string): Promise<void> {
    // Generate the invoice via the print-job API, then download the PDF.
    const jobResp = await this.client.post<{ job: { id: number; pdf_url: string } }>(
      '/print/jobs',
      { document_type: 'invoice', entity_type: 'order', entity_ids: [orderId] }
    );
    const pdfUrl = jobResp.data.job.pdf_url.replace(/^\/api/, '');
    const pdfResp = await this.client.get(pdfUrl, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${orderNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getOrderCommunications(orderId: number): Promise<{
    order_id: number;
    order_number: string;
    communications: {
      id: number;
      timestamp: string;
      type: string;
      action: string;
      subject: string;
      to: string;
      from: string;
      status: 'sent' | 'failed';
      error?: string;
      notes?: string;
    }[];
  }> {
    const response = await this.client.get(`/orders/${orderId}/communications`);
    return response.data;
  }

  async getOrderActivityLog(orderId: number): Promise<{
    order_id: number;
    order_number: string;
    activities: {
      id: number;
      timestamp: string;
      action: string;
      description: string;
      user_id?: number;
      user_name?: string;
      notes?: string;
      old_values?: any;
      new_values?: any;
    }[];
  }> {
    const response = await this.client.get(`/orders/${orderId}/activity-log`);
    return response.data;
  }

  async bulkSendPickupEmail(orderIds: number[], from?: string): Promise<{
    message: string;
    queued: number;
    skipped: number;
    failed: number;
    queued_orders: { id: number; order_number: string; email: string; task_id: string }[];
    skipped_orders: { id: number; reason: string }[];
    failed_orders: { id: number; reason: string }[];
    from: string;
  }> {
    const response = await this.client.post<{
      message: string;
      queued: number;
      skipped: number;
      failed: number;
      queued_orders: { id: number; order_number: string; email: string; task_id: string }[];
      skipped_orders: { id: number; reason: string }[];
      failed_orders: { id: number; reason: string }[];
      from: string;
    }>('/orders/bulk-send-pickup-email', { order_ids: orderIds, from });
    return response.data;
  }

  async bulkProcessPickups(orderIds: number[], emailTemplate: string, from?: string): Promise<{
    message: string;
    dispatched: number;
    dispatched_order_ids?: number[];
    emails_queued: number;
    invoice_url: string | null;
    dg_url: string | null;
    skipped: number;
    skipped_orders: { id: number; reason: string }[];
  }> {
    const response = await this.client.post<{
      message: string;
      dispatched: number;
      dispatched_order_ids?: number[];
      emails_queued: number;
      invoice_url: string | null;
      dg_url: string | null;
      skipped: number;
      skipped_orders: { id: number; reason: string }[];
    }>('/orders/bulk-process-pickups', { order_ids: orderIds, email_template: emailTemplate, from });
    return response.data;
  }

  async bulkBookShipments(orderIds: number[], payload: Record<string, any>): Promise<{ task_id: string; total: number }> {
    const response = await this.client.post<{ task_id: string; total: number }>('/shipments/bulk-book', {
      order_ids: orderIds,
      ...payload,
    });
    return response.data;
  }

  async getBulkBookingStatus(taskId: string): Promise<{
    state: string;
    step?: string;
    total?: number | null;
    completed?: number;
    result?: {
      total: number;
      succeeded: number;
      failed: number;
      booked_order_ids: number[];
      errors: { order_id: number; order_number?: string; error: string }[];
    };
    error?: string;
  }> {
    const response = await this.client.get<{
      state: string;
      step?: string;
      total?: number | null;
      completed?: number;
      result?: {
        total: number;
        succeeded: number;
        failed: number;
        booked_order_ids: number[];
        errors: { order_id: number; order_number?: string; error: string }[];
      };
      error?: string;
    }>(`/shipments/bulk-book/${taskId}`);
    return response.data;
  }

  async dispatchPickupInstore(orderId: number): Promise<{ message: string; order_number: string; status: string; shipped_at: string }> {
    const response = await this.client.post<{ message: string; order_number: string; status: string; shipped_at: string }>(
      `/orders/${orderId}/dispatch-pickup-instore`
    );
    return response.data;
  }

  // Email Templates
  async getEmailTemplates(): Promise<{ name: string; filename: string; updated_at: number }[]> {
    const response = await this.client.get<{ templates: { name: string; filename: string; updated_at: number }[] }>('/email-templates');
    return response.data.templates || [];
  }

  async getEmailTemplate(name: string): Promise<{ name: string; filename: string; content: string }> {
    const response = await this.client.get<{ name: string; filename: string; content: string }>(`/email-templates/${encodeURIComponent(name)}`);
    return response.data;
  }

  async updateEmailTemplate(name: string, content: string): Promise<{ message: string; name: string; filename: string }> {
    const response = await this.client.put<{ message: string; name: string; filename: string }>(
      `/email-templates/${encodeURIComponent(name)}`,
      { content }
    );
    return response.data;
  }

  async previewEmailTemplate(name: string, sampleData?: Record<string, any>): Promise<{ html: string }> {
    const response = await this.client.post<{ html: string }>(
      `/email-templates/${encodeURIComponent(name)}/preview`,
      sampleData ? { sample_data: sampleData } : {}
    );
    return response.data;
  }

  // Order Item Management
  async addOrderItem(orderId: number, itemData: {
    product_id: number;
    quantity: number;
    unit_price?: number;
  }): Promise<{ message: string; order: Order }> {
    const response = await this.client.post<{ message: string; order: Order }>(
      `/orders/${orderId}/items`,
      itemData
    );
    return response.data;
  }

  async updateOrderItem(orderId: number, itemId: number, itemData: {
    quantity?: number;
    unit_price?: number;
  }): Promise<{ message: string; order: Order }> {
    const response = await this.client.put<{ message: string; order: Order }>(
      `/orders/${orderId}/items/${itemId}`,
      itemData
    );
    return response.data;
  }

  async deleteOrderItem(orderId: number, itemId: number): Promise<{ message: string; order: Order }> {
    const response = await this.client.delete<{ message: string; order: Order }>(
      `/orders/${orderId}/items/${itemId}`
    );
    return response.data;
  }

  async getOrderStats(): Promise<OrderStats> {
    const response = await this.client.get<OrderStats>('/orders/stats');
    return response.data;
  }

  // Products & Inventory
  async getProducts(filters: InventoryFilters = {}): Promise<ApiResponse<Product[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{ products: Product[], pagination: any }>(`/inventory/products?${params}`);
    return {
      data: response.data.products,
      pagination: response.data.pagination
    };
  }

  async getProduct(id: number): Promise<Product> {
    const response = await this.client.get<{ product: Product }>(`/products/${id}`);
    return response.data.product;
  }

  async getInventoryLevels(filters: InventoryFilters = {}): Promise<ApiResponse<InventoryLevel[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<ApiResponse<InventoryLevel[]>>(`/inventory?${params}`);
    return response.data;
  }

  async createProduct(productData: any): Promise<Product> {
    const response = await this.client.post<Product>('/products', productData);
    return response.data;
  }

  async updateProduct(id: number, productData: any): Promise<Product> {
    const response = await this.client.put<Product>(`/products/${id}`, productData);
    return response.data;
  }

  async deleteProduct(id: number): Promise<void> {
    await this.client.delete(`/products/${id}`);
  }

  async adjustStock(productId: number, adjustment: StockAdjustmentForm): Promise<InventoryLevel> {
    const response = await this.client.post<{ inventory_level: InventoryLevel }>(
      `/products/${productId}/adjust-stock`,
      adjustment
    );
    return response.data.inventory_level;
  }

  // Fulfillment
  async evaluateFulfillment(orderId: number, options?: Partial<FulfillmentForm>): Promise<FulfillmentOptions> {
    const response = await this.client.post<{ fulfillment: FulfillmentOptions }>(
      `/fulfill/${orderId}`,
      options || {}
    );
    return response.data.fulfillment;
  }

  async executeFulfillment(orderId: number, options: FulfillmentForm): Promise<any> {
    const response = await this.client.post(`/fulfill/${orderId}/execute`, options);
    return response.data;
  }

  async getFulfillmentLocations(): Promise<any[]> {
    const response = await this.client.get<{ locations: any[] }>('/fulfillment-locations');
    return response.data.locations;
  }

  async getCourierOptions(): Promise<any[]> {
    const response = await this.client.get<{ couriers: any[] }>('/courier-options');
    return response.data.couriers;
  }

  // Shipments
  async getShipments(filters: any = {}): Promise<ApiResponse<Shipment[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<ApiResponse<Shipment[]>>(`/shipments?${params}`);
    return response.data;
  }

  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private normalizeIdempotencyKey(key?: string): string {
    if (!key) {
      return this.generateUUID();
    }
    // The backend accepts UUIDs or URL-safe strings between 8 and 128 chars.
    const urlSafePattern = /^[A-Za-z0-9_-]{8,128}$/;
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(key) || urlSafePattern.test(key)) {
      return key;
    }
    // If the caller provided an unsupported key, fall back to a fresh UUID so
    // the request is not rejected outright.
    return this.generateUUID();
  }

  async createShipment(orderId: number, shipmentData: any, idempotencyKey?: string): Promise<Shipment> {
    const headers: Record<string, string> = {
      'Idempotency-Key': this.normalizeIdempotencyKey(idempotencyKey),
    };
    const response = await this.client.post<{ shipment: Shipment }>(
      `/shipment/${orderId}`,
      shipmentData,
      { headers }
    );
    return response.data.shipment;
  }

  async rebookShipment(shipmentId: number, shipmentData: any, idempotencyKey?: string): Promise<Shipment> {
    const headers: Record<string, string> = {
      'Idempotency-Key': this.normalizeIdempotencyKey(idempotencyKey),
    };
    const response = await this.client.post<{ shipment: Shipment }>(
      `/shipments/${shipmentId}/rebook`,
      shipmentData,
      { headers }
    );
    return response.data.shipment;
  }

  async logClientEvent(level: string, message: string, context?: Record<string, any>): Promise<void> {
    try {
      await this.client.post('/client-log', { level, message, context });
    } catch (e) {
      // Never break the user flow if client logging fails
      console.warn('Failed to send client log', e);
    }
  }

  async getShipmentTracking(shipmentId: number): Promise<TrackingEvent[]> {
    const response = await this.client.get<{ tracking_events: TrackingEvent[] }>(
      `/shipments/${shipmentId}/tracking`
    );
    return response.data.tracking_events;
  }

  async trackByNumber(trackingNumber: string): Promise<any> {
    const response = await this.client.get(`/tracking/${trackingNumber}`);
    return response.data;
  }

  // Deliveries
  async getDeliveries(filters: any = {}): Promise<{ deliveries: any[], pagination: any }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
    const response = await this.client.get<{ deliveries: any[], pagination: any }>(`/deliveries?${params}`);
    return response.data;
  }

  async getDelivery(id: string): Promise<any> {
    const response = await this.client.get(`/deliveries/${id}`);
    return response.data;
  }

  async createDelivery(data: any): Promise<any> {
    const response = await this.client.post('/deliveries', data);
    return response.data;
  }

  async updateDelivery(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/deliveries/${id}`, data);
    return response.data;
  }

  async deleteDelivery(id: string): Promise<void> {
    await this.client.delete(`/deliveries/${id}`);
  }

  async startDeliveryById(id: string): Promise<any> {
    const response = await this.client.post(`/deliveries/${id}/start`);
    return response.data;
  }

  async completeDeliveryById(id: string): Promise<any> {
    const response = await this.client.post(`/deliveries/${id}/complete`);
    return response.data;
  }

  async deliverStop(deliveryId: string, stopId: string, podData: any): Promise<any> {
    const response = await this.client.post(`/deliveries/${deliveryId}/stops/${stopId}/deliver`, {
      proof_of_delivery: podData
    });
    return response.data;
  }

  async reportDeliveryException(deliveryId: string, type: string, description: string): Promise<any> {
    const response = await this.client.post(`/deliveries/${deliveryId}/exceptions`, { type, description });
    return response.data;
  }

  async getDeliveryStats(): Promise<any> {
    const response = await this.client.get('/deliveries/stats');
    return response.data;
  }

  // Routes
  async getRoutes(date: string, driverId?: number): Promise<{ routes: Route[] }> {
    const params = new URLSearchParams();
    if (driverId) params.append('driver_id', String(driverId));

    const response = await this.client.get<{ routes: Route[] }>(`/routes/${date}?${params}`);
    return response.data;
  }

  async createRoute(routeData: any): Promise<Route> {
    const response = await this.client.post<{ route: Route }>('/routes', routeData);
    return response.data.route;
  }

  async startRoute(routeId: number): Promise<Route> {
    const response = await this.client.post<{ route: Route }>(`/routes/${routeId}/start`);
    return response.data.route;
  }

  async completeDelivery(routeId: number, deliveryId: number, data: any): Promise<any> {
    const response = await this.client.post(
      `/routes/${routeId}/deliveries/${deliveryId}/complete`,
      data
    );
    return response.data;
  }

  async getVehicles(): Promise<Vehicle[]> {
    const response = await this.client.get<{ vehicles: Vehicle[], pagination: any }>('/vehicles');
    return response.data.vehicles;
  }

  async createVehicle(vehicleData: any): Promise<Vehicle> {
    const response = await this.client.post<{ vehicle: Vehicle }>('/vehicles', vehicleData);
    return response.data.vehicle;
  }

  async updateVehicle(id: number | string, vehicleData: any): Promise<Vehicle> {
    const response = await this.client.put<{ vehicle: Vehicle }>(`/vehicles/${id}`, vehicleData);
    return response.data.vehicle;
  }

  async deleteVehicle(id: number | string): Promise<void> {
    await this.client.delete(`/vehicles/${id}`);
  }

  // Customers
  async getCustomers(filters: any = {}): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{customers: any[], pagination: any}>(`/customers?${params}`);
    return {
      data: response.data.customers,
      pagination: response.data.pagination
    };
  }

  async getCustomer(id: number): Promise<any> {
    const response = await this.client.get<{ customer: any }>(`/customers/${id}`);
    return response.data.customer;
  }

  async createCustomer(customerData: any): Promise<any> {
    const response = await this.client.post<{ customer: any }>('/customers', customerData);
    return response.data.customer;
  }

  async updateCustomer(id: number, customerData: any): Promise<any> {
    const response = await this.client.put<{ customer: any }>(`/customers/${id}`, customerData);
    return response.data.customer;
  }

  async deleteCustomer(id: number): Promise<void> {
    await this.client.delete(`/customers/${id}`);
  }

  // Quotes
  async getQuotes(filters: any = {}): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{quotes: any[], pagination: any}>(`/quotes?${params}`);
    return {
      data: response.data.quotes,
      pagination: response.data.pagination
    };
  }

  async getQuote(id: number): Promise<any> {
    const response = await this.client.get(`/quotes/${id}`);
    return response.data;
  }

  async createQuote(quoteData: any): Promise<any> {
    const response = await this.client.post<{ quote: any }>('/quotes', quoteData);
    return response.data.quote;
  }

  async updateQuote(id: number, quoteData: any): Promise<any> {
    const response = await this.client.put<{ quote: any }>(`/quotes/${id}`, quoteData);
    return response.data.quote;
  }

  async updateQuoteStatus(id: number, status: string): Promise<any> {
    const response = await this.client.patch<{ quote: any }>(`/quotes/${id}/status`, { status });
    return response.data.quote;
  }

  async convertQuoteToOrder(id: number, data: any = {}): Promise<any> {
    const response = await this.client.post<{ order_id: number; order_number: string }>(`/quotes/${id}/convert`, data);
    return response.data;
  }

  async deleteQuote(id: number): Promise<void> {
    await this.client.delete(`/quotes/${id}`);
  }

  // Drivers
  async getDrivers(filters: any = {}): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{ drivers: any[], data: any[], pagination: any }>(`/drivers?${params}`);
    return {
      data: response.data.drivers || response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getDriver(id: number | string): Promise<any> {
    const response = await this.client.get(`/drivers/${id}`);
    return response.data;
  }

  async createDriver(driverData: any): Promise<any> {
    const response = await this.client.post('/drivers', driverData);
    return response.data;
  }

  async updateDriver(id: number | string, driverData: any): Promise<any> {
    const response = await this.client.put(`/drivers/${id}`, driverData);
    return response.data;
  }

  async deleteDriver(id: number | string): Promise<void> {
    await this.client.delete(`/drivers/${id}`);
  }

  async getRouteTracking(routeId: string): Promise<any> {
    const response = await this.client.get(`/tracking/${routeId}`);
    return response.data;
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const [orderStats, shipmentStats, dashboardStats] = await Promise.all([
      this.getOrderStats(),
      this.client.get('/shipments/stats'),
      this.client.get('/dashboard/stats'),
    ]);

    const stats = dashboardStats.data;
    return {
      orders: orderStats,
      shipments: {
        in_transit: shipmentStats.data.status_counts?.in_transit || 0,
        delivered_today: shipmentStats.data.today_shipments || 0,
        failed_deliveries: shipmentStats.data.status_counts?.failed_delivery || 0,
      },
      inventory: {
        low_stock_items: stats?.products?.total - stats?.products?.available || 0,
        total_products: stats?.products?.total || 0,
        locations: 0,
      },
      routes: {
        active_routes: 0,
        completed_deliveries: 0,
        pending_deliveries: 0,
      },
    };
  }

  // Webhooks (Admin only)
  async getWebhookEvents(filters: WebhookFilters = {}): Promise<ApiResponse<WebhookEvent[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<ApiResponse<WebhookEvent[]>>(
      `/webhooks/events?${params}`
    );
    return response.data;
  }

  async getWebhookEvent(id: number): Promise<WebhookEvent> {
    const response = await this.client.get<{ event: WebhookEvent }>(`/webhooks/events/${id}`);
    return response.data.event;
  }

  async getWebhookStats(): Promise<WebhookStats> {
    const response = await this.client.get<WebhookStats>('/webhooks/stats');
    return response.data;
  }

  async retryWebhookEvent(id: number): Promise<WebhookEvent> {
    const response = await this.client.post<{ event: WebhookEvent }>(`/webhooks/events/${id}/retry`);
    return response.data.event;
  }

  async deleteWebhookEvent(id: number): Promise<void> {
    await this.client.delete(`/webhooks/events/${id}`);
  }

  async bulkRetryWebhooks(filters: WebhookFilters): Promise<{ retried_count: number }> {
    const response = await this.client.post<{ retried_count: number }>('/webhooks/bulk-retry', filters);
    return response.data;
  }

  async testWebhook(source: string, event_type: string, payload: Record<string, any>): Promise<WebhookEvent> {
    const response = await this.client.post<{ event: WebhookEvent }>('/webhooks/test', {
      source,
      event_type,
      payload
    });
    return response.data.event;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; app: string; version: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Shipping Rates
  async getShippingRates(filters: any = {}): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await this.client.get<{rates: any[], pagination: any}>(`/shipping-rates?${params}`);
    return {
      data: response.data.rates,
      pagination: response.data.pagination
    };
  }

  async calculateShippingRate(data: {
    order_items: any[];
    total_weight?: number;
    order_value?: number;
    distance_km?: number;
    zone_code?: string;
    shipping_method?: string;
  }): Promise<any> {
    const response = await this.client.post('/shipping-rates/calculate', data);
    return response.data;
  }

  async getShippingMethods(): Promise<any[]> {
    const response = await this.client.get('/shipping-rates/methods');
    return response.data.methods;
  }

  async getLiveShippingQuote(data: {
    courier_service: string;
    service_type?: string;
    package_count: number;
    total_weight: number;
    length?: number;
    width?: number;
    height?: number;
    pickup_city?: string;
    pickup_postcode?: string;
    pickup_state?: string;
    delivery_city?: string;
    delivery_postcode?: string;
    delivery_state?: string;
    delivery_address_line1?: string;
    recipient_name?: string;
  }): Promise<{ services: any[]; courier_service: string }> {
    // Vellex quotes scrape the portal and can take 30-60s — allow up to 90s
    const timeout = data.courier_service === 'vellex' ? 90000 : undefined;
    const response = await this.client.post('/shipments/quote', data, { timeout });
    return response.data;
  }

  // Google Places API proxy
  async getPlaceSuggestions(input: string, country: string = 'au'): Promise<{ predictions: any[]; error?: string }> {
    const response = await this.client.get(`/places/autocomplete?input=${encodeURIComponent(input)}&country=${country}`);
    return response.data;
  }

  async getPlaceDetails(placeId: string): Promise<any> {
    const response = await this.client.get(`/places/details/${placeId}`);
    return response.data;
  }

  // Generic HTTP methods for direct API calls
  async get<T = any>(url: string): Promise<T> {
    const response = await this.client.get<T>(url);
    return response.data;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  // Routing Rules
  async getRoutingRules(filters: { is_active?: boolean } = {}): Promise<{ rules: any[]; count: number }> {
    const params = new URLSearchParams();
    if (filters.is_active !== undefined) {
      params.append('is_active', String(filters.is_active));
    }
    const response = await this.client.get(`/routing-rules?${params}`);
    return response.data;
  }

  async createRoutingRule(data: any): Promise<{ success: boolean; rule: any }> {
    const response = await this.client.post('/routing-rules', data);
    return response.data;
  }

  async updateRoutingRule(id: number, data: any): Promise<{ success: boolean; rule: any }> {
    const response = await this.client.put(`/routing-rules/${id}`, data);
    return response.data;
  }

  async deleteRoutingRule(id: number): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/routing-rules/${id}`);
    return response.data;
  }

  async enableRoutingRule(id: number): Promise<{ success: boolean; rule: any }> {
    const response = await this.client.post(`/routing-rules/${id}/enable`);
    return response.data;
  }

  async disableRoutingRule(id: number): Promise<{ success: boolean; rule: any }> {
    const response = await this.client.post(`/routing-rules/${id}/disable`);
    return response.data;
  }

  async getConditionTypes(): Promise<{ success: boolean; condition_types: any[] }> {
    const response = await this.client.get('/routing-rules/condition-types');
    return response.data;
  }

  async getActionTypes(): Promise<{ success: boolean; action_types: any[] }> {
    const response = await this.client.get('/routing-rules/action-types');
    return response.data;
  }

  async reorderRoutingRules(rules: Array<Record<string, number>>): Promise<{ success: boolean }> {
    const response = await this.client.post('/routing-rules/reorder', { rules });
    return response.data;
  }

  async testRoutingRule(id: number, orderId: number): Promise<{ success: boolean; test_result: any }> {
    const response = await this.client.post(`/routing-rules/${id}/test`, { order_id: orderId });
    return response.data;
  }

  async evaluateRoutingRules(orderId: number, dryRun = false, stopOnMatch = false): Promise<{ success: boolean; evaluation: any }> {
    const response = await this.client.post('/routing-rules/evaluate', {
      order_id: orderId,
      dry_run: dryRun,
      stop_on_match: stopOnMatch,
    });
    return response.data;
  }

  async reshuffleOrders(dryRun = false, orderIds?: number[]): Promise<{ success: boolean; dry_run: boolean; total_evaluated: number; moved: number; unchanged: number; errors: number; details: any[] }> {
    const response = await this.client.post('/routing-rules/reshuffle', {
      dry_run: dryRun,
      order_ids: orderIds,
    });
    return response.data;
  }

  // Utility methods
  getCurrentUser(): User | null {
    return tokenManager.getUser();
  }

  isAuthenticated(): boolean {
    return tokenManager.isAuthenticated();
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  canAccess(resource: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    const permissions = {
      admin: ['all'],
      manager: ['orders', 'inventory', 'routes', 'reports', 'users'],
      warehouse_staff: ['orders', 'inventory', 'picking'],
      driver: ['routes', 'deliveries'],
      customer_service: ['orders', 'customers', 'tracking'],
      readonly: ['view_only'],
    };

    const userPermissions = permissions[user.role as keyof typeof permissions] || [];
    return userPermissions.includes('all') || userPermissions.includes(resource);
  }
}

export const apiService = new ApiService();
export default apiService;